import { IMAGES, CONTACT } from "./assets";
import { SERVICES, SOLUTIONS, PROCESS } from "./data";

export const SITE_URL = "https://lakemonitor.ai";
export const SITE_NAME = "Lake Monitor AI";
const OG_IMAGE = IMAGES.hero;

export const PAGES = {
  home: {
    path: "/",
    title: "Lake Monitor AI | Bathymetric Lake & Reservoir Mapping Services (US & Canada)",
    description:
      "Full-service lake, pond and reservoir mapping. We collect survey-grade sonar data on-site and deliver finished depth (bathymetric), aquatic vegetation, bottom hardness, fish habitat and GIS maps across the United States and Canada.",
    keywords: "bathymetric mapping, lake mapping services, reservoir depth survey, aquatic vegetation mapping, sonar lake survey, pond depth map, GIS lake map, lake volume calculation",
  },
  services: {
    path: "/services",
    title: "Lake Mapping Services: Bathymetry, Vegetation, Bottom Hardness & Habitat | Lake Monitor AI",
    description:
      "Explore our sonar-based lake mapping services: bathymetric depth contours and volume, aquatic vegetation biovolume, bottom composition and hardness, fish and habitat mapping, side-scan imagery, GIS deliverables and survey planning.",
  },
  solutions: {
    path: "/solutions",
    title: "Lake & Reservoir Mapping for Water Districts, Municipalities, HOAs & Pond Owners | Lake Monitor AI",
    description:
      "Tailored survey solutions for water resource districts, community and municipal lakes, HOAs and lake associations, and private pond owners. Track sedimentation, plan dredging, manage vegetation and share professional maps.",
  },
  howItWorks: {
    path: "/how-it-works",
    title: "How a Lake Survey Works: Plan, On-Site Sonar Collection, Processing, Delivered Maps | Lake Monitor AI",
    description:
      "Our four-step full-service process: consultation and survey planning, on-site sonar and GPS data collection, hydrographer quality review, and delivery of GIS-ready, cloud-accessible maps and reports.",
  },
  about: {
    path: "/about",
    title: "About Lake Monitor AI | Full-Service Hydrographic Lake Mapping Company",
    description:
      "Lake Monitor AI is a full-service lake and reservoir mapping company serving the United States and Canada, combining survey-grade sonar, GPS and modern processing to make the underwater world visible and actionable.",
  },
  quote: {
    path: "/request-a-quote",
    title: "Request a Lake Mapping Quote | Lake Monitor AI",
    description:
      "Tell us about your lake, pond or reservoir and your goals. We'll reply with a tailored survey plan and quote for depth, vegetation, sediment or habitat mapping. No obligation.",
  },
  admin: {
    path: "/admin",
    title: "Leads Portal | Lake Monitor AI",
    description: "Internal leads dashboard.",
    noindex: true,
  },
  adminAccount: {
    path: "/admin/account",
    title: "Account | Lake Monitor AI",
    description: "Internal admin account settings.",
    noindex: true,
  },
  adminBrand: {
    path: "/admin/brand",
    title: "Brand Assets | Lake Monitor AI",
    description: "Internal logo downloads.",
    noindex: true,
  },
  adminSeoPlan: {
    path: "/admin/seo-plan",
    title: "SEO Promotion Plan | Lake Monitor AI",
    description: "Internal SEO promotion plan.",
    noindex: true,
  },
  adminSeo: {
    path: "/admin/seo-checklist",
    title: "SEO Submission Checklist | Lake Monitor AI",
    description: "Internal search-engine submission checklist.",
    noindex: true,
  },
  legal_privacy_policy: { path: "/privacy-policy", title: "Privacy Policy | Lake Monitor AI", description: "How Lake Monitor AI collects, uses and protects the personal information you share when requesting a lake mapping quote." },
  legal_terms_of_service: { path: "/terms-of-service", title: "Terms of Service | Lake Monitor AI", description: "Terms that apply when you use lakemonitor.ai and request lake, pond or reservoir mapping services from Lake Monitor AI." },
  legal_cookie_policy: { path: "/cookie-policy", title: "Cookie Policy | Lake Monitor AI", description: "What cookies and browser storage lakemonitor.ai uses, why, and how you can control them. Essential storage only; analytics only with consent." },
  legal_accessibility: { path: "/accessibility", title: "Accessibility Statement | Lake Monitor AI", description: "Lake Monitor AI's commitment to WCAG 2.1 AA accessibility on lakemonitor.ai, known limitations and how to request information in alternative formats." },
  locations: {
    path: "/locations",
    title: "Lake Mapping Service Areas: All 50 US States & 10 Canadian Provinces | Lake Monitor AI",
    description:
      "Lake Monitor AI provides on-site bathymetric, vegetation, bottom-hardness and habitat mapping for lakes, ponds and reservoirs in every US state and Canadian province. Find your region.",
  },
};

export const regionMeta = (r, d) => ({
  path: `/lake-mapping/${r.slug}`,
  title: `Lake Mapping in ${r.name} | Bathymetric, Depth & Vegetation Surveys (${r.abbr}) | Lake Monitor AI`,
  description: `Full-service sonar lake mapping in ${r.name}: bathymetric depth maps, lake volume, aquatic vegetation, bottom hardness, fish habitat and GIS deliverables for ${r.waterbodies.slice(0, 3).join(", ")} and lakes near ${d ? d.cities.slice(0, 3).join(", ") : r.name}. We travel to you — request a quote.`,
  keywords: `lake mapping ${r.name}, bathymetric survey ${r.name}, ${r.abbr} lake depth map, reservoir survey ${r.name}, aquatic vegetation mapping ${r.name}, pond mapping ${r.name}, lake volume survey ${r.name}, sediment survey ${r.name}${d ? ", " + d.cities.map((c) => `lake mapping ${c}`).join(", ") : ""}`,
});

export const regionSchema = (r, faq, d) => {
  const country = { "@type": "Country", name: r.country === "US" ? "United States" : "Canada" };
  const area = { "@type": r.country === "US" ? "State" : "AdministrativeArea", name: r.name, containedInPlace: country };
  const served = d ? [area, ...d.cities.map((c) => ({ "@type": "City", name: c, containedInPlace: area }))] : area;
  return [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${SITE_URL}/lake-mapping/${r.slug}#service`,
      name: `Lake & reservoir mapping in ${r.name}`,
      serviceType: "Bathymetric and hydrographic lake mapping",
      description: regionMeta(r, d).description,
      provider: { "@id": `${SITE_URL}/#organization` },
      areaServed: served,
      url: `${SITE_URL}/lake-mapping/${r.slug}`,
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: `Mapping services available in ${r.name}`,
        itemListElement: SERVICES.map((s) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: `${s.title} in ${r.name}`, areaServed: area } })),
      },
    },
    faqSchema(faq),
  ];
};

export const regionFaq = (r, d) => [
  { q: `Do you travel to ${r.name} for lake surveys?`, a: `Yes. Lake Monitor AI is a full-service company that travels to waterbodies across ${r.name} with our own survey boat, sonar and GPS equipment. You do not need to supply a boat, equipment or staff.` },
  { q: `When is the best time to survey a lake in ${r.name}?`, a: `Most ${r.name} surveys are scheduled ${r.season}. Depth and bottom-hardness mapping can be done any time the water is open; aquatic vegetation mapping is best timed to peak plant growth so the biovolume data reflects the real extent of the beds.` },
  { q: `What does a lake mapping survey in ${r.name} cost?`, a: `Pricing depends on surface area, the layers you need (depth only, or depth plus vegetation, hardness and habitat), site access and travel within ${r.name}. Every project is quoted individually. Request a quote and we will respond with a tailored plan and price with no obligation.` },
  { q: `Which agencies or permits are involved in ${r.name}?`, a: `Sonar mapping is non-invasive and does not normally require a permit. If your project supports a dredging, aquatic plant management or dam-safety application, we format deliverables to suit the requirements typically referenced by the ${r.agency} and local authorities.` },
  { q: `How small or large a waterbody can you map in ${r.name}?`, a: `Anything from a one-acre farm pond or HOA lake to a reservoir with hundreds of miles of shoreline. Small ponds are usually completed in a few hours on site; large ${r.name} reservoirs such as ${r.waterbodies[0]} are surveyed in phases or by priority area. The workflow, accuracy and deliverables are the same at every scale.` },
  { q: `What deliverables will I receive from a ${r.name} lake survey?`, a: `A depth-contour map (PDF and GIS), volume and stage-storage tables, surface-area statistics, and — depending on the layers ordered — aquatic vegetation biovolume maps, bottom-hardness and composition maps, fish habitat layers and side-scan imagery. Files are delivered as shapefiles, GeoTIFFs and PDFs plus a written summary you can share with boards, engineers or the ${r.agency}.` },
  ...(d ? [{ q: `Do you serve ${d.cities[0]}, ${d.cities[1]} and the rest of ${r.name}?`, a: `Yes. We regularly work around ${d.cities.join(", ")} and travel to every corner of ${r.name}. Travel is included in the quote up front, and we combine nearby lakes into a single trip whenever possible to keep costs down.` }] : []),
  { q: `Can you compare a new ${r.name} survey with an older map?`, a: `Yes. If you have a previous bathymetric map, capacity table or vegetation survey, we georeference it and produce change maps showing sediment accumulation, capacity loss or vegetation expansion between the two dates — the same before-and-after view shown in our interactive sample map.` },
];

export const absolute = (path) => `${SITE_URL}${path === "/" ? "" : path}`;

export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": ["Organization", "ProfessionalService"],
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  alternateName: "Lake Monitor",
  url: SITE_URL,
  logo: IMAGES.logo,
  image: OG_IMAGE,
  description: PAGES.home.description,
  email: CONTACT.email,
  telephone: CONTACT.phone,
  areaServed: [
    { "@type": "Country", name: "United States" },
    { "@type": "Country", name: "Canada" },
  ],
  knowsAbout: [
    "Bathymetric surveying",
    "Hydrographic surveying",
    "Aquatic vegetation mapping",
    "Sonar mapping",
    "GIS mapping",
    "Reservoir sedimentation",
    "Lake management",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "sales",
    email: CONTACT.email,
    telephone: CONTACT.phone,
    areaServed: ["US", "CA"],
    availableLanguage: "English",
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Lake & Reservoir Mapping Services",
    itemListElement: SERVICES.map((s) => ({
      "@type": "Offer",
      itemOffered: { "@type": "Service", name: s.title, url: `${SITE_URL}/services#${s.slug}` },
    })),
  },
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: SITE_NAME,
  publisher: { "@id": `${SITE_URL}/#organization` },
  inLanguage: "en-US",
});

export const servicesSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Lake Monitor AI mapping services",
  itemListElement: SERVICES.map((s, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: {
      "@type": "Service",
      "@id": `${SITE_URL}/services#${s.slug}`,
      name: s.title,
      description: s.desc,
      serviceType: s.title,
      provider: { "@id": `${SITE_URL}/#organization` },
      areaServed: ["US", "CA"],
      image: s.image,
      offers: { "@type": "Offer", availability: "https://schema.org/InStock", url: `${SITE_URL}/request-a-quote` },
    },
  })),
});

export const audienceSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Who Lake Monitor AI serves",
  itemListElement: SOLUTIONS.map((s, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: { "@type": "Audience", audienceType: s.audience, name: s.title, description: s.pains.join(". ") },
  })),
});

export const howToSchema = () => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How a Lake Monitor AI lake survey works",
  description: PAGES.howItWorks.description,
  step: PROCESS.map((p, i) => ({ "@type": "HowToStep", position: i + 1, name: p.title, text: p.desc })),
});

export const faqSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const breadcrumbSchema = (crumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map(([name, path], i) => ({
    "@type": "ListItem",
    position: i + 1,
    name,
    item: absolute(path),
  })),
});

export const OG_DEFAULT_IMAGE = OG_IMAGE;
